/* eslint-disable @typescript-eslint/no-explicit-any */
import { APIProvider } from "@vis.gl/react-google-maps";
import { CalendarIcon } from "lucide-react";
import { matchIsValidTel, MuiTelInput } from "mui-tel-input";
import { format } from "date-fns";
import Image from "next/image";

import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormControl, FormField, FormItem, FormMessage } from "../ui/form";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import CustomFormLabel from "../other/CustomFormLabel";
import { cn } from "@/lib/utils";
import { Button as CNButton } from "@/components/ui/button";
import SiteInputRAQB from "./SiteInput";
import AppText from "../other/AppText";
import { FieldError } from "react-hook-form";
import { useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { ChevronDown } from "lucide-react";
import { AddIcon, Info24 } from "@/assets/Icons";
import { useMediaQuery } from "@/hooks/mediaQuery";

export const FieldTextArea = ({
  control,
  name,
  placeholder,
  loading,
  label,
}: {
  control: any;
  name: string;
  placeholder: string;
  loading: boolean;
  label?: string;
}) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => {
      return (
        <FormItem className="flex-1 space-y-0">
          {label && <CustomFormLabel label={label} />}
          <Textarea
            disabled={loading}
            aria-label="additional info"
            placeholder={placeholder}
            maxLength={5000}
            {...field}
            className="font-saira border-[#898A8D] rounded-[5px]"
          />
          <FormMessage className="font-saira text-14 font-[400]" />
        </FormItem>
      );
    }}
  />
);

export const FieldGoogleAddress = ({
  control,
  label,
  setSelectedPlaceBuy,
  deliveryAddressBlur,
  handleDeliveryAddressOnChange,
  placeholder,
  loading,
  name,
  className,
}: {
  control: any;
  label: string;
  setSelectedPlaceBuy: any;
  deliveryAddressBlur: any;
  handleDeliveryAddressOnChange: any;
  placeholder: string;
  loading: boolean;
  name: string;
  className?: string;
}) => (
  <APIProvider apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API!}>
    <div className="flex-1">
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState: { error } }) => {
          return (
            <FormItem className="flex-1 space-y-0">
              <div className="h-0 overflow-hidden">
                <FormControl>
                  <Input
                    tabIndex={-1}
                    {...field}
                    className="hiddenInput"
                    autoComplete="nope"
                    role="presentation"
                    aria-describedby={undefined}
                  />
                </FormControl>
              </div>

              <SiteInputRAQB
                className={cn("border-[#898A8D] rounded-[5px]", className)}
                label={label}
                setSelectedPlace={setSelectedPlaceBuy}
                deliveryAddressBlur={deliveryAddressBlur}
                // error={!!errors?.selectedPlace}
                handleDeliveryAddressOnChange={handleDeliveryAddressOnChange}
                placeholder={placeholder}
                disabled={loading}
                error={!!error}
              />
              <FormMessage className="font-saira text-14 font-[400]" />
            </FormItem>
          );
        }}
      />
    </div>
  </APIProvider>
);

export const FieldCalendar = ({
  control,
  loading,
  name,
  placeholder,
  label,
  className,
}: {
  control: any;
  loading: boolean;
  name: string;
  placeholder: string;
  label: string;
  className?: string;
}) => (
  <FormField
    control={control}
    name={name}
    render={({ field, fieldState: { error } }) => {
      return (
        <FormItem className="flex flex-col flex-1 space-y-0">
          <span
            className={cn("font-saira text-14 font-[400] tracking-[-0.1px] leading-[1.7]", error && "text-red-500")}
          >
            {label}
          </span>
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <CNButton
                  aria-describedby={undefined}
                  ref={field.ref}
                  disabled={loading}
                  variant={"outline"}
                  className={cn(
                    "pl-3 text-left font-normal font-saira text-16 border-[#898A8D] focus-visible:ring-blue-500/50 rounded-[5px]",
                    !field.value && "text-muted-foreground",
                    error && "border-red-500",
                    className
                  )}
                >
                  {field.value ? format(field.value, "PPP") : <span className="text-[#64748b]">{placeholder}</span>}
                  <CalendarIcon className="w-4 h-4 ml-auto opacity-50" aria-label="calendar" />
                </CNButton>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={field.value}
                onSelect={field.onChange}
                disabled={(date) => date < new Date() || date > new Date("2100-01-01")}
                initialFocus
                className="font-saira"
              />
            </PopoverContent>
          </Popover>

          <FormMessage className="font-saira text-14 font-[400]" />
        </FormItem>
      );
    }}
  />
);

export const FieldTextMultiple = ({
  control,
  loading,
  name,
  placeholder,
  label,
  footnote,
  className,
  type,
  handleEvent,
  textFields,
  setTextFields,
  handleRemoveInvoice,
  AddInvoiceLabel,
}: {
  control: any;
  loading: boolean;
  name: string;
  placeholder: string;
  label: string;
  footnote?: string;
  className?: string;
  type?: string;
  handleEvent?: () => void;
  textFields?: any;
  setTextFields?: (values: any) => void;
  handleRemoveInvoice: (name: any, value: any) => void;
  AddInvoiceLabel?: string;
}) => {
  return (
    <div className="flex flex-col flex-1">
      {/* <div className="flex w-full"> */}
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState: { error } }) => {
          return (
            <FormItem className={cn("flex-1 space-y-0", className)}>
              <div className="flex flex-row w-full">
                <div className="w-full">
                  <CustomFormLabel label={label} />
                  <FormControl>
                    <Input
                      placeholder={placeholder}
                      {...field}
                      disabled={loading}
                      type={type}
                      aria-describedby={undefined}
                      aria-invalid={error ? true : false}
                      className={cn(
                        "font-saira border-[#898A8D] rounded-[5px] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                        error && "border-red-500"
                      )}
                      onBlur={handleEvent}
                      id="abcdefgh"
                    />
                  </FormControl>
                  <FormMessage className="font-saira text-14 font-[400]" />
                </div>
              </div>
            </FormItem>
          );
        }}
      />
      {textFields?.map((item: any) => (
        <div className="flex flex-col w-full" key={item.id}>
          <FormField
            control={control}
            name={item.name}
            render={({ field, fieldState: { error } }) => {
              return (
                <FormItem className={cn("flex-1 space-y-0", className)}>
                  <div className="flex flex-row w-full">
                    <div className="w-full">
                      <CustomFormLabel label={label} />
                      <FormControl>
                        <div className="flex flex-row items-center justify-center">
                          <Input
                            placeholder={placeholder}
                            {...field}
                            disabled={loading}
                            type={type}
                            aria-describedby={undefined}
                            aria-invalid={error ? true : false}
                            className={cn(
                              "font-saira border-[#898A8D] rounded-[5px] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                              error && "border-red-500"
                            )}
                            onBlur={handleEvent}
                            id={item.name}
                          />
                          <div
                            className="ml-2 cursor-pointer"
                            onClick={() => {
                              handleRemoveInvoice(field.name, field.value);
                            }}
                          >
                            <AppText type={"BODY_SMALL_EMPHASIS"}>X</AppText>
                          </div>
                        </div>
                      </FormControl>
                      <FormMessage className="font-saira text-14 font-[400]" />
                    </div>
                  </div>
                </FormItem>
              );
            }}
          />
        </div>
      ))}
      <div
        onClick={() => {
          const id = crypto.randomUUID();
          setTextFields && setTextFields((prev: any) => [...prev, { id: id, name: `PAI_Invoice_${id}` }]);
        }}
        className="flex mt-4 cursor-pointer"
      >
        <div className="flex border-1 rounded-lg border border-[#898A8D] flex-row justify-center items-center px-2">
          <AddIcon />
          <AppText type={"BODY_SMALL"}> {AddInvoiceLabel}</AppText>
        </div>
      </div>

      {footnote && (
        <div className="flex mt-2">
          <Info24 />
          <AppText className="text-[#697780]" type="BODY_SMALL_EMPHASIS">
            {footnote}
          </AppText>
        </div>
      )}
    </div>
  );
};

export const FieldText = ({
  control,
  loading,
  name,
  placeholder,
  label,
  footnote,
  className,
  type,
}: {
  control: any;
  loading: boolean;
  name: string;
  placeholder: string;
  label: string;
  footnote?: string;
  className?: string;
  type?: string;
}) => (
  <FormField
    control={control}
    name={name}
    render={({ field, fieldState: { error } }) => (
      <FormItem className={cn("flex-1 space-y-0", className)}>
        <CustomFormLabel label={label} />
        <FormControl>
          <Input
            placeholder={placeholder}
            {...field}
            disabled={loading}
            type={type}
            aria-describedby={undefined}
            aria-invalid={error ? true : false}
            className={cn(
              "font-saira border-[#898A8D] rounded-[5px] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
              error && "border-red-500"
            )}
          />
        </FormControl>
        <FormMessage className="font-saira text-14 font-[400]" />
        {footnote && (
          <AppText className="lg:col-span-2" type="CAPTION_MEDIUM">
            {footnote}
          </AppText>
        )}
      </FormItem>
    )}
  />
);

export const FieldPhone = ({
  control,
  loading,
  name,
  placeholder,
  label,
}: {
  control: any;
  loading: boolean;
  name: string;
  placeholder: string;
  label: string;
}) => (
  <FormField
    control={control}
    name={name}
    rules={{
      validate: (value) =>
        matchIsValidTel(value ?? "", {
          onlyCountries: ["US", "CA"],
        }),
    }}
    render={({ field: { value, ref, onChange, ...fieldProps }, fieldState }) => (
      <FormItem className="flex flex-col flex-1 space-y-0">
        <CustomFormLabel label={label} />

        {/** helper input for scroll to error */}
        <div className="h-0 overflow-hidden">
          <FormControl>
            <Input
              aria-label="telInputScrollTarget"
              id="telInputScrollTarget"
              className="hiddenInput"
              ref={ref}
              tabIndex={-1}
              autoComplete="nope"
              aria-describedby={undefined}
              // type="presentation"
            />
          </FormControl>
        </div>

        <div
          className={cn(
            "telInputCustom focus-within:ring-[2px] ring-offset-2 bg-white ring-blue-500/50 outline-none",
            fieldState.error && "!border-red-500"
          )}
        >
          <FormControl>
            <MuiTelInput
              aria-describedby={undefined}
              {...fieldProps}
              inputProps={{ style: { padding: "0.5rem", backgroundColor: "white", borderColor: "#898A8D" } }}
              defaultCountry="US"
              forceCallingCode
              onlyCountries={["US", "CA"]}
              placeholder={placeholder}
              value={value ?? ""}
              disabled={loading}
              onChange={(currentValue) => {
                onChange(currentValue);
                // if (currentValue.startsWith("+1")) {
                //   onChange(currentValue);
                // } else if (currentValue.startsWith("+")) {
                //   const newValue = currentValue.replace("+", "+1");
                //   onChange(newValue);
                // } else {
                //   onChange("+1" + currentValue);
                // }
              }}
              aria-invalid={fieldState.error ? true : false}
              error={fieldState.invalid ? true : false}
              disableDropdown
            />
          </FormControl>
        </div>
        <FormMessage className="font-saira text-14 font-[400]" />
      </FormItem>
    )}
  />
);

interface IOptionIndustry {
  id: string;
  description: string;
  internalLabel: string;
  key: string;
  optionType: string;
  title: string;
}

export const FieldDropdownIndustries = ({
  control,
  name,
  placeholder,
  label,
  options,
}: {
  control: any;
  name: string;
  placeholder: string;
  label: string;
  options: any;
}) => (
  <>
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        return (
          <FormItem className="w-full space-y-0">
            <p className={cn("font-saira text-14 font-[400] tracking-[-0.1px] leading-[1.7]", error && "text-red-500")}>
              {label}
            </p>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger
                  // title="Choose one of the industries"
                  extra={options.find((ind: IOptionIndustry) => ind.key === field.value)?.title}
                  className={cn(
                    "!mt-0 placeholder:text-[#64748b] border-[#898A8D] rounded-[5px]",
                    error && "border-red-500"
                  )}
                  aria-describedby={undefined}
                  {...field}
                >
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>

                <SelectContent className="selectContentRadix">
                  <SelectGroup className="flex flex-col gap-2">
                    {/* <SelectLabel className="hidden1">{label}</SelectLabel> */}

                    {options.map((ind: IOptionIndustry) => (
                      <SelectItem key={ind.id} value={ind.key} className="overflow-hidden">
                        <div className=" cursor-pointer">
                          <div className="font-saira font-medium text-[#24262E] tracking-[-0.1px] font-size-4">
                            {ind.title}
                          </div>
                          {ind.description && (
                            <div className="font-saira text-[#24262E] tracking-[-0.1px] font-size-4 break-all whitespace-wrap w-full">
                              {ind.description}
                            </div>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage className="font-saira text-14 font-[400]" />
            {/* {form?.formState?.errors?.quote_industries && (
            <p className="text-red-500 font-saira text-14 font-[400]">{formCopy["required"]}</p>
          )} */}
          </FormItem>
        );
      }}
    />
  </>
);

export const FieldFallbackIndustries = ({
  control,
  name,
  placeholder,
  label,
  watchIndustry,
}: {
  control: any;
  name: string;
  placeholder: string;
  label: string;
  watchIndustry: string;
}) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        return (
          <FormItem className={cn("flex-1 w-full space-y-0", watchIndustry === "Other" ? "block" : "hidden")}>
            <p className={cn("font-saira text-14 font-[400] tracking-[-0.1px] leading-[1.7]", error && "text-red-500")}>
              {label}
            </p>
            <FormControl>
              <Input
                aria-label="other industry"
                placeholder={placeholder}
                className={cn("border-[#898A8D] rounded-[5px]", error && "border-red-500")}
                autoComplete="nope"
                {...field}
                aria-describedby={undefined}
              />
            </FormControl>
            <FormMessage className="font-saira text-14 font-[400]" />
          </FormItem>
        );
      }}
    />
  );
};

const FieldMultiSelect = ({ form, name, label, options, placeholder }: any) => {
  const searchParams = useSearchParams();
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const error = form.formState.errors[name]?.message;
  const initializedRef = useRef(false);
  const lastCategoryRef = useRef<string | null>(null);
  const productCategory = searchParams.get("product_category");
  const formattedKey = productCategory?.replace(/_/g, " ");
  const optionData = options.find((option: any) => option.key.toLowerCase() === formattedKey?.toLowerCase());
  const productCategoryValue = optionData ? optionData.key : null;

  useEffect(() => {
    const hasUserChanged = initializedRef.current;

    if (productCategoryValue && lastCategoryRef.current !== productCategoryValue) {
      setSelectedValues([productCategoryValue]);
      form.setValue(name, [productCategoryValue]);
      lastCategoryRef.current = productCategoryValue;
    } else if (!productCategoryValue && !hasUserChanged && selectedValues.length === 0) {
      const defaultOption = options.find((opt: any) => opt.key.toLowerCase() === "storage containers");
      if (defaultOption) {
        setSelectedValues([defaultOption.key]);
        form.setValue(name, [defaultOption.key]);
        lastCategoryRef.current = defaultOption.key;
      }
    }
  }, [productCategoryValue, form, name, options, selectedValues.length]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (value: string) => {
    initializedRef.current = true;

    setSelectedValues((prev) => {
      let newValues;
      if (prev.includes(value)) {
        newValues = prev.filter((v) => v !== value);
      } else {
        newValues = [...prev, value];
      }

      form.setValue(name, newValues);
      form.trigger(name);
      return newValues;
    });
  };

  const displayText =
    selectedValues.length > 2
      ? `${options.find((opt: { key: string }) => opt.key === selectedValues[0])?.title}, ${
          options.find((opt: { key: string }) => opt.key === selectedValues[1])?.title
        }, ...`
      : selectedValues
          .map((key) => options.find((opt: { key: string }) => opt.key === key)?.title)
          .filter(Boolean)
          .join(", ") || placeholder;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <p className={cn("font-saira text-14 font-[400] tracking-[-0.1px] leading-[1.7]", error && "text-red-500")}>
        {label}
      </p>
      <button
        type="button"
        className={cn(
          `flex h-10 w-full items-center justify-between rounded-md border ${
            error ? "border-red-500" : "border-slate-200"
          }
          bg-white px-3 py-2 text-sm ring-offset-white focus:outline-none
          focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-2 border-[#898A8D] placeholder:text-[#64748b] rounded-[5px]`
        )}
        onClick={() => setOpen(!open)}
      >
        {displayText}
        <ChevronDown className="w-4 h-4 opacity-50" />
      </button>

      {open && (
        <div className="max-h-60 border-slate-200 absolute z-50 flex flex-col w-full mt-1 overflow-auto bg-white border rounded-md shadow-lg">
          {isDesktop ? (
            <div className="flex-1 overflow-auto">
              {options.map((option: any) => (
                <label key={option.key} className="hover:bg-slate-100 flex items-center p-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.key)}
                    onChange={() => handleSelect(option.key)}
                    className="w-4 h-4 mr-2"
                  />
                  {option.title}
                </label>
              ))}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto">
                {options.map((option: any) => (
                  <label key={option.key} className="hover:bg-slate-100 flex items-center p-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(option.key)}
                      onChange={() => handleSelect(option.key)}
                      className="w-4 h-4 mr-2"
                    />
                    {option.title}
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="hover:text-gray-700 border-slate-200 flex items-center justify-center gap-2 py-2 text-sm text-gray-500 border-t"
                style={{ backgroundColor: "#00205B", color: "#FFFFFF", height: "31px" }}
                onClick={() => setOpen(false)}
              >
                <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M10.5 11.4408L6.33492 15.6058C6.20565 15.7351 6.05368 15.7976 5.879 15.7933C5.70433 15.7891 5.54969 15.7196 5.41508 15.585C5.28046 15.4504 5.21314 15.2936 5.21314 15.1147C5.21314 14.9357 5.28046 14.7789 5.41508 14.6443L9.55931 10.5001L5.39425 6.33499C5.26497 6.20572 5.20247 6.05028 5.20675 5.86865C5.21101 5.68704 5.28046 5.52893 5.41508 5.39432C5.54969 5.2597 5.70647 5.19238 5.88542 5.19238C6.06436 5.19238 6.22114 5.2597 6.35575 5.39432L10.5 9.55938L14.6651 5.39432C14.7943 5.26504 14.9498 5.19907 15.1314 5.1964C15.313 5.19372 15.4711 5.2597 15.6057 5.39432C15.7404 5.52893 15.8077 5.68571 15.8077 5.86465C15.8077 6.0436 15.7404 6.20038 15.6057 6.33499L11.4407 10.5001L15.6057 14.6652C15.735 14.7944 15.801 14.9464 15.8037 15.1211C15.8063 15.2957 15.7404 15.4504 15.6057 15.585C15.4711 15.7196 15.3144 15.7869 15.1354 15.7869C14.9565 15.7869 14.7997 15.7196 14.6651 15.585L10.5 11.4408Z"
                    fill="white"
                  />
                </svg>
                <span>Close</span>
              </button>
            </>
          )}
          {error && <FormMessage className="font-saira text-14 font-[400]">{error}</FormMessage>}
        </div>
      )}

      {error && <FormMessage className="font-saira text-14 font-[400]">{error}</FormMessage>}
    </div>
  );
};

export { FieldMultiSelect };

interface IOptionDuration {
  id: string;
  key: string;
  title: string;
  internalLabel: string;
  optionType: string;
}

export const FieldDropdown = ({
  control,
  name,
  placeholder,
  label,
  options,
}: {
  control: any;
  name: string;
  placeholder: string;
  label: string;
  options: IOptionDuration[];
}) => {
  const searchParams = useSearchParams();
  const productCategory = searchParams.get("product_category");
  const formattedKey = productCategory?.replace(/_/g, " ");
  const optionData = options.find((optionValue: any) => optionValue.key.toLowerCase() === formattedKey?.toLowerCase());
  const productCategoryValue = optionData ? optionData.key : "";

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          field.onChange(productCategoryValue);
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [productCategoryValue]);
        return (
          <FormItem className="flex-1 space-y-0">
            <p className={cn("font-saira text-14 font-[400] tracking-[-0.1px] leading-[1.7]", error && "text-red-500")}>
              {label}
            </p>
            <FormControl>
              <Select
                onValueChange={field.onChange}
                value={
                  productCategoryValue === formattedKey
                    ? productCategoryValue || field.value
                    : field.value || productCategoryValue
                }
              >
                <SelectTrigger
                  className={cn(
                    "!mt-0 border-[#898A8D] placeholder:text-[#64748b] rounded-[5px]",
                    error && "border-red-500"
                  )}
                  ref={field.ref}
                  title={label}
                >
                  <SelectValue placeholder={placeholder} className="placeholder:text-[#64748b]" />
                </SelectTrigger>

                <SelectContent
                  style={{
                    position: "absolute",
                    zIndex: 1300,
                    top: "auto",
                    maxHeight: "300px",
                    overflowY: "auto",
                  }}
                >
                  {options.map((dur: IOptionDuration) => (
                    <SelectItem aria-label={dur.title} key={dur.id} value={dur.key}>
                      {dur.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage className="font-saira text-14 font-[400]" />
          </FormItem>
        );
      }}
    />
  );
};

export const FieldOptionsCards = ({
  group,
  control,
  name,
  handleCheckboxes,
  checkBoxCard,
  updateRadioButtons,
  radioCardsState,
}: {
  group: any;
  control: any;
  name: string;
  handleCheckboxes: (item: string, checked: boolean) => void;
  checkBoxCard: any;
  updateRadioButtons: (group: string, value: string, del: boolean) => void;
  radioCardsState: any;
}) => {
  const { cards = [] } = group;
  return (
    <>
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState: { error } }) => {
          return (
            <FormItem className="flex-1 space-y-0 overflow-hidden">
              <div className="h-0 overflow-hidden">
                <FormControl>
                  <Input
                    aria-label="cards target"
                    {...field}
                    autoComplete="nope"
                    role="presentation"
                    className="hiddenInput"
                    aria-describedby={undefined}
                  />
                </FormControl>
              </div>
              {/* {error ? (

                <FormMessage className="font-saira text-14 font-[400] mb-3" />
              ) : (
                <p className="font-saira text-14 font-[400] mb-3">
                  {error?.message}
                </p>
              )} */}

              {/* <FormMessage className="font-saira text-14 font-[400] mb-3" /> */}
              <p className="font-saira text-14 font-[400] mb-3 text-red-500 formMessage_error">
                {error?.message || ""}
              </p>
              <div className={cn("sm:grid-cols-8 grid grid-cols-4 gap-3 pt-1")}>
                {cards.map(
                  ({
                    id,
                    name: cardName,
                    image,
                    heading,
                    options,
                    subheading,
                    label = "",
                    style,
                  }: {
                    id: string;
                    name: string;
                    image: string;
                    heading: string;
                    options: { id: string; key: string; value: string }[];
                    subheading: string;
                    style: string;
                    label?: string;
                  }) => {
                    if (style === "Checkboxes") {
                      return (
                        <FormCardCheckbox
                          key={id}
                          title={heading}
                          label={label}
                          img={image}
                          description={subheading}
                          checkboxes={options}
                          checkBoxCard={checkBoxCard}
                          handleCheckboxes={handleCheckboxes}
                          error={error}
                        />
                      );
                    } else {
                      return (
                        <FormCardRadio
                          key={id}
                          title={heading}
                          label={label}
                          img={image}
                          description={subheading}
                          updateRadioButtons={updateRadioButtons}
                          options={options}
                          groupName={cardName}
                          radioCardsState={radioCardsState}
                          error={error}
                        />
                      );
                    }
                  }
                )}
              </div>
            </FormItem>
          );
        }}
      />
    </>
  );
};

export const FormCardCheckbox = ({
  title,
  label,
  img,
  description,
  checkboxes,
  handleCheckboxes,
  checkBoxCard,
  error,
}: {
  title: string;
  label: string;
  img: string;
  description: string;
  checkboxes: any;
  handleCheckboxes: (item: string, checked: boolean) => void;
  checkBoxCard: any;
  error: FieldError | undefined;
}) => {
  return (
    <div
      data-focus-mark="card-mark"
      className={cn(
        "bg-navy-blue-10 lg:col-span-3 flex flex-col col-span-4 p-4 border-[1px] border-transparent",
        error && "border-red-500"
      )}
    >
      {label && (
        <AppText type="BODY_SMALL_EMPHASIS" className="text-royal-blue text-end font-[600] uppercase">
          {label}
        </AppText>
      )}
      <Image
        src={img}
        sizes="100vw"
        width={0}
        height={0}
        className="flex-1 object-cover w-full max-w-[220px] mx-auto"
        alt={title}
      />

      <div className="flex flex-col flex-1 mb-5">
        <AppText type="SUB_HEADING_SMALL" className="text-wrap text-18">
          {title}
        </AppText>
        <AppText type="BODY_SMALL_EMPHASIS" className="text-wrap font-[400]">
          {description}
        </AppText>
      </div>

      <fieldset className="checkBoxContainer flex flex-col">
        <legend className="hidden">radio buttons</legend>
        {checkboxes.map((item: { id: string; key: string; value: string }) => {
          return (
            <div key={item.id} className="flex flex-row items-center py-[2px] space-x-3 space-y-0">
              <input
                id={item.id}
                autoComplete="nope"
                type="checkbox"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckboxes(item.key, e.target.checked)}
                checked={checkBoxCard[item.key] || false}
                className={cn(
                  "min-w-4 shrink-0 bg-[#e6e9ef] cursor-pointer border-black border-[1px] w-4 h-4 focus-visible:ring-blue-500/50 ring-2 ring-transparent outline-none focus-visible:outline-none",
                  checkBoxCard[item.key] ? "accent-black" : "appearance-none"
                )}
              ></input>
              <label htmlFor={item.id} className="space-y-1 leading-none font-[400] font-saira text-15 cursor-pointer">
                {item.value}
              </label>
            </div>
          );
        })}
      </fieldset>
    </div>
  );
};

export const FormCardRadio = ({
  title,
  img,
  updateRadioButtons,
  options,
  groupName = "",
  radioCardsState,
  description,
  label,
  error,
}: {
  title: string;
  description: string;
  img: string;
  options: { id: string; key: string; value: string }[];
  updateRadioButtons: (group: string, value: string, del: boolean) => void;
  groupName: string | undefined;
  radioCardsState: any;
  label?: string;
  error: FieldError | undefined;
}) => {
  const handleRadioClick = (e: any) => {
    const value = e.target.value;

    /**
     * groupName is the name of the card inside state (work_and_collaborate, educate_train.....)
     * value is the value of the radio button (small, medium, large)
     * third argument is a boolean to determine if the radio button should be deleted (true) or added (false)
     * this logic is because of requirement to uncheck radio button (usually that's not possible, in group must be one checked)
     */
    if (radioCardsState[groupName]) {
      if (radioCardsState[groupName] === value) {
        updateRadioButtons(groupName, value, true);
      } else {
        updateRadioButtons(groupName, value, false);
      }
    } else {
      updateRadioButtons(groupName, value, false);
    }

    // if (value) {
    //   if (value === groupName) {
    //     // Check if the value is the same as the current value

    //     updateRadioButtons(title, value, true);
    //   } else {
    //     updateRadioButtons(title, value, false);
    //   }
    // } else {
    //   if (groupName) {
    //     updateRadioButtons(title, groupName, true);
    //   }
    // }
  };

  return (
    <div
      data-focus-mark="card-mark"
      className={cn(
        "bg-navy-blue-10 lg:col-span-3 flex flex-col col-span-4 p-4 border-[1px] border-transparent",
        error && "border-red-500"
      )}
    >
      {label && (
        <AppText type="BODY_SMALL_EMPHASIS" className="text-royal-blue text-end font-[600] uppercase">
          {label}
        </AppText>
      )}

      {/** add support for blur when refactoring to contentful data */}
      <Image
        src={img}
        sizes="100vw"
        width={0}
        height={0}
        className="flex-1 object-cover w-full max-w-[220px] mx-auto"
        alt={title}
      />

      <div className="flex flex-col flex-1 mb-5">
        <AppText type="SUB_HEADING_SMALL" className="text-wrap text-18">
          {title}
        </AppText>
        <AppText type="BODY_SMALL_EMPHASIS" className="text-wrap font-[400]">
          {description}
        </AppText>
      </div>
      <fieldset className="flex flex-col" id={title}>
        <legend className="hidden">{title}</legend>

        {options.map((item) => {
          // const id = `radio-group-${item}`;

          return (
            <div role="radiogroup" key={item.id} className="flex flex-row items-center py-[2px] space-x-3 space-y-0">
              <input
                id={item.id}
                autoComplete="nope"
                type="radio"
                checked={radioCardsState[groupName] === item.key}
                name={title}
                value={item.key}
                onChange={() => {}}
                onClick={handleRadioClick}
                className="min-w-4 focus-visible:ring-blue-500/50 ring-2 ring-transparent focus-visible:outline-none w-4 h-4 outline-none"
              />
              <label className="font-[400] font-saira text-15 cursor-pointer" htmlFor={item.id}>
                {item.value}
              </label>
            </div>
          );
        })}
      </fieldset>
    </div>
  );
};
